import { renderPrompt } from '../utils/templating';
import { z } from 'zod';
import { zodTextFormat } from 'openai/helpers/zod';
import { categoryToVistaMapping } from 'constants/evaluation';

const API_BASE_URL =
  import.meta.env.VITE_CMS_BASEURL || 'https://content.srx.id';
const CMS_API_TOKEN = import.meta.env.VITE_CMS_API_TOKEN;

// Zod schema for aspect scores
const AspectScoreSchema = z.object({
  aspect_id: z
    .string()
    .describe('The unique identifier for the scoring aspect'),
  aspect_name: z.string().describe('The name of the scoring aspect'),
  score: z
    .number()
    .int()
    .min(0)
    .max(10)
    .describe('The numerical score from 0 to 10'),
  comment: z
    .string()
    .describe('2-3 concise bullet points explaining the score'),
});

// Zod schema for the complete judgment response
const JudgmentSchema = z.object({
  aspect_scores: z
    .array(AspectScoreSchema)
    .describe('Array of scores for each judging aspect'),
  overall_summary: z
    .string()
    .describe(
      '2-3 bullet points highlighting key strengths and areas for improvement'
    ),
  key_strengths: z
    .array(z.string())
    .min(1)
    .max(5)
    .describe('List of key strengths identified in the submission'),
  areas_for_improvement: z
    .array(z.string())
    .min(1)
    .max(5)
    .describe('List of areas where the submission could be improved'),
});

/**
 * Fetch all VISTA projects (award categories) with complete pagination
 * Uses Strapi pagination with maximum 100 results per page
 */
export const fetchVistaProjects = async (year: number) => {
  let allData = [];
  let page = 1;
  let hasMorePages = true;
  const pageSize = 100; // Maximum recommended page size

  // Add filters
  let filterParams = '&filters[type][$eq]=award_category';

  if (year) {
    const startDate = `${year}-01-01T00:00:00.000Z`;
    const endDate = `${year}-12-31T23:59:59.999Z`;
    filterParams += `&filters[createdAt][$gte]=${encodeURIComponent(startDate)}&filters[createdAt][$lte]=${encodeURIComponent(endDate)}`;
  }

  while (hasMorePages) {
    const response = await fetch(
      `${API_BASE_URL}/api/vista-projects?pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true${filterParams}`,
      {
        headers: {
          Authorization: `Bearer ${CMS_API_TOKEN}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch projects (page ${page}): ${response.statusText}`
      );
    }

    const result = await response.json();

    if (!result.data || !Array.isArray(result.data)) {
      throw new Error(`Invalid projects data received from API (page ${page})`);
    }

    // Add current page data to total
    allData = allData.concat(result.data);

    // Check pagination metadata for more pages
    const pagination = result.meta?.pagination;
    if (pagination) {
      hasMorePages = page < pagination.pageCount;
    } else {
      hasMorePages = false;
    }

    page++;
  }

  return { data: allData };
};

/**
 * Fetch all VISTA rubrics with complete pagination
 * Uses Strapi pagination with maximum 100 results per page
 */
export const fetchVistaRubrics = async (year: number) => {
  let allData = [];
  let page = 1;
  let hasMorePages = true;
  const pageSize = 100; // Maximum recommended page size

  // Add year filter if provided
  let filterParams = '';
  if (year) {
    const startDate = `${year}-01-01T00:00:00.000Z`;
    const endDate = `${year}-12-31T23:59:59.999Z`;
    filterParams = `&filters[createdAt][$gte]=${encodeURIComponent(startDate)}&filters[createdAt][$lte]=${encodeURIComponent(endDate)}`;
  }

  while (hasMorePages) {
    const response = await fetch(
      `${API_BASE_URL}/api/vista-rubrics?pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true${filterParams}`,
      {
        headers: {
          Authorization: `Bearer ${CMS_API_TOKEN}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch rubrics (page ${page}): ${response.statusText}`
      );
    }

    const result = await response.json();

    if (!result.data || !Array.isArray(result.data)) {
      throw new Error(`Invalid rubrics data received from API (page ${page})`);
    }

    // Add current page data to total
    allData = allData.concat(result.data);

    // Check pagination metadata for more pages
    const pagination = result.meta?.pagination;
    if (pagination) {
      hasMorePages = page < pagination.pageCount;
    } else {
      hasMorePages = false;
    }

    page++;
  }

  return { data: allData };
};

/**
 * Fetch all VISTA aspects with complete pagination
 * Uses Strapi pagination with maximum 100 results per page
 */
export const fetchVistaAspects = async (year: number) => {
  let allData = [];
  let page = 1;
  let hasMorePages = true;
  const pageSize = 100; // Maximum recommended page size

  // Add year filter if provided
  let filterParams = '';
  if (year) {
    const startDate = `${year}-01-01T00:00:00.000Z`;
    const endDate = `${year}-12-31T23:59:59.999Z`;
    filterParams = `&filters[createdAt][$gte]=${encodeURIComponent(startDate)}&filters[createdAt][$lte]=${encodeURIComponent(endDate)}`;
  }

  while (hasMorePages) {
    const response = await fetch(
      `${API_BASE_URL}/api/vista-aspects?pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true${filterParams}`,
      {
        headers: {
          Authorization: `Bearer ${CMS_API_TOKEN}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch aspects (page ${page}): ${response.statusText}`
      );
    }

    const result = await response.json();

    if (!result.data || !Array.isArray(result.data)) {
      throw new Error(`Invalid aspects data received from API (page ${page})`);
    }

    // Add current page data to total
    allData = allData.concat(result.data);

    // Check pagination metadata for more pages
    const pagination = result.meta?.pagination;
    if (pagination) {
      hasMorePages = page < pagination.pageCount;
    } else {
      hasMorePages = false;
    }

    page++;
  }

  return { data: allData };
};

/**
 * Get complete judging data for an award category
 */
export const getJudgingDataForCategory = async (
  categoryName: string,
  year: number
) => {
  // 1. Fetch all data in parallel
  const [projectsResponse, rubricsResponse, aspectsResponse] =
    await Promise.all([
      fetchVistaProjects(year),
      fetchVistaRubrics(year),
      fetchVistaAspects(year),
    ]);

  const projects = projectsResponse.data || [];
  const rubrics = rubricsResponse.data || [];
  const aspects = aspectsResponse.data || [];

  const vistaCategoryName = categoryToVistaMapping(categoryName);

  if (!vistaCategoryName) {
    throw new Error(`Category "${categoryName}" not found in mapping`);
  }

  // 3. Find the matching project in Vista API
  const project = projects.find(p => p.name === vistaCategoryName);

  if (!project) {
    throw new Error(
      `Vista project "${vistaCategoryName}" not found for category "${categoryName}"`
    );
  }

  if (!project.rubric_document_id) {
    throw new Error(`No rubric found for category "${categoryName}"`);
  }

  // 4. Find the rubric
  const rubric = rubrics.find(r => r.documentId === project.rubric_document_id);

  if (!rubric) {
    throw new Error(`Rubric not found for category "${categoryName}"`);
  }

  // 5. Get the aspects for this rubric
  if (!rubric.aspect_list || !Array.isArray(rubric.aspect_list)) {
    throw new Error(
      `No aspects found in rubric for category "${categoryName}"`
    );
  }

  const enrichedAspects = rubric.aspect_list
    .map(aspectItem => {
      const aspect = aspects.find(a => a.documentId === aspectItem.aspect_id);
      if (!aspect) {
        console.warn(
          `Aspect not found for ID: ${aspectItem.aspect_id} in rubric ${rubric.name}`
        );
        return null;
      }
      return {
        ...aspect,
        weight: aspectItem.weight,
      };
    })
    .filter(Boolean);

  if (enrichedAspects.length === 0) {
    throw new Error(
      `No valid aspects found for rubric "${rubric.name}" in category "${categoryName}"`
    );
  }

  return {
    project,
    rubric,
    aspects: enrichedAspects,
  };
};

/**
 * Generate AI judgment for a submission
 */
export const generateAIJudgment = async (
  submissionData,
  judgingData,
  recaptchaToken?: string
) => {
  // Prepare template data
  const templateData = {
    award_category: judgingData.project.name,
    submission_title: submissionData.title,
    full_name: submissionData.full_name,
    submission_type: submissionData.submission_type,
    org_unit: submissionData.org_unit,
    team_members: Array.isArray(submissionData.team_members)
      ? submissionData.team_members.join(', ')
      : submissionData.team_members || '',
    innovation_description: submissionData.innovation_description || '',
    technology_used: submissionData.technology_used || '',
    impact_and_results: submissionData.impact_and_results || '',
    scalability_potential: submissionData.scalability_potential || '',
    leadership_achievement: submissionData.leadership_achievement || '',
    environmental_impact: submissionData.environmental_impact || '',
    additional_comments: submissionData.additional_comments || '',
    aspects: judgingData.aspects.map((aspect, index, array) => ({
      aspect_id: aspect.documentId,
      aspect_name: aspect.name,
      aspect_description: aspect.description,
      weight: aspect.weight,
      scale_min: aspect.scale_min,
      scale_max: aspect.scale_max,
      scale_guidance: Object.entries(aspect.scale_guidance || {}).map(
        ([score, description]) => ({
          score,
          description,
        })
      ),
      last: index === array.length - 1,
    })),
  };

  // Load and render the judging prompt template
  const promptTemplate =
    await import('../prompts/vista_judging_prompt.mustache?raw');
  const prompt = renderPrompt(promptTemplate.default, templateData);

  // Call the VISTA AI proxy

  // Using OpenAI's zodTextFormat helper for consistent structured outputs

  const response = await fetch(`${API_BASE_URL}/api/vista-ai/responses`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${CMS_API_TOKEN}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      input: prompt,
      text: {
        format: zodTextFormat(JudgmentSchema, 'award_judgment'),
      },
      recaptchaToken: recaptchaToken || 'dev-bypass',
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `AI judgment failed: ${response.statusText} - ${errorText}`
    );
  }

  const aiResponse = await response.json();

  // Extract the text response from the AI
  const judgmentText = aiResponse.output?.[0]?.content?.[0]?.text || '';

  // Parse and validate JSON response using Zod schema
  let judgmentData;
  try {
    // Parse JSON
    const parsedJson = JSON.parse(judgmentText);

    // Validate using Zod schema
    judgmentData = JudgmentSchema.parse(parsedJson);

    // Calculate weighted total score
    if (
      judgmentData.aspect_scores &&
      Array.isArray(judgmentData.aspect_scores)
    ) {
      let totalWeightedScore = 0;
      let totalWeight = 0;

      judgmentData.aspect_scores.forEach(aspectScore => {
        // Find the weight for this aspect
        const aspect = judgingData.aspects.find(
          a => a.documentId === aspectScore.aspect_id
        );
        if (aspect && aspect.weight) {
          const weight = aspect.weight; // Weight is already in percentage (0-100)
          const score = aspectScore.score;
          const weightedScore = score * weight;

          totalWeightedScore += weightedScore;
          totalWeight += weight;
        }
      });

      // Calculate final weighted average
      const finalScore = totalWeight > 0 ? totalWeightedScore / totalWeight : 0;
      judgmentData.weighted_total_score = finalScore;
    } else {
      judgmentData.weighted_total_score = 0;
    }
  } catch (parseError) {
    if (parseError.name === 'ZodError') {
      console.error('AI judgment validation failed:', parseError.errors);
      throw new Error(
        `AI response validation failed: ${parseError.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`
      );
    }
    console.error('Failed to parse AI judgment response:', parseError);
    throw new Error(
      `Failed to parse or validate AI response: ${parseError.message}`
    );
  }

  return {
    aiResponse: judgmentText,
    judgmentData,
    prompt: prompt,
  };
};

/**
 * Submit a judgment to the VISTA system
 */
export const submitVistaJudgment = async (
  submissionDocumentId,
  judgmentData,
  judgingData,
  judgeInfo,
  recaptchaToken?: string
) => {
  const vistaJudgment = {
    submission_document_id: submissionDocumentId,
    submission_table: 'award-submission',
    rubric_document_id: judgingData.rubric.documentId,
    aspect_scores: judgmentData.aspect_scores.map(score => ({
      aspect_id: score.aspect_id,
      value: score.score,
      comment: Array.isArray(score.comment)
        ? score.comment.join(' ')
        : score.comment,
    })),
    overall_summary: Array.isArray(judgmentData.overall_summary)
      ? judgmentData.overall_summary.join(' ')
      : judgmentData.overall_summary,
    judge_name: judgeInfo.name || 'AI Judge',
    judge_email: judgeInfo.email || 'ai@sinarmas.com',
    judged_at: new Date().toISOString(),
  };

  const response = await fetch(`${API_BASE_URL}/api/vista-judgments`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${CMS_API_TOKEN}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data: vistaJudgment }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    try {
      JSON.parse(errorText);
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(
      `Failed to submit judgment: ${response.statusText} - ${errorText}`
    );
  }

  return await response.json();
};

/**
 * Get leaderboard for an award category
 */
export const getLeaderboard = async projectDocumentId => {
  const response = await fetch(
    `${API_BASE_URL}/api/vista-projects/${projectDocumentId}/leaderboard`,
    {
      headers: {
        Authorization: `Bearer ${CMS_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch leaderboard: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Get submission summary with scores
 */
export const getSubmissionSummary = async (
  projectDocumentId,
  submissionDocumentId
) => {
  const response = await fetch(
    `${API_BASE_URL}/api/vista-projects/${projectDocumentId}/summary/${submissionDocumentId}`,
    {
      headers: {
        Authorization: `Bearer ${CMS_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch submission summary: ${response.statusText}`
    );
  }

  return await response.json();
};

/**
 * End-to-end function: Generate and submit AI judgment for a submission
 * This is the main function other files should use to consume judgment functionality
 */
export const processAIJudgmentForSubmission = async (
  submissionDocumentId: string,
  judgeInfo: any = {},
  recaptchaToken?: string
) => {
  try {
    // 1. Fetch the submission
    const submissionResponse = await fetch(
      `${API_BASE_URL}/api/award-submissions/${submissionDocumentId}`,
      {
        headers: {
          Authorization: `Bearer ${CMS_API_TOKEN}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!submissionResponse.ok) {
      throw new Error(
        `Failed to fetch submission: ${submissionResponse.statusText}`
      );
    }

    const submissionData = await submissionResponse.json();
    const submission = submissionData.data;

    // Determine year from submission createdAt if available
    const year = submission.createdAt
      ? new Date(submission.createdAt).getFullYear()
      : undefined;

    // 2. Get judging data for the submission's category
    const judgingData = await getJudgingDataForCategory(
      submission.category,
      year
    );

    // 3. Generate AI judgment
    const judgment = await generateAIJudgment(
      submission,
      judgingData,
      recaptchaToken
    );

    // 4. Submit the judgment to VISTA API
    if (!judgment.judgmentData) {
      throw new Error('AI failed to generate valid judgment data');
    }

    const submittedJudgment = await submitVistaJudgment(
      submission.documentId,
      judgment.judgmentData,
      judgingData,
      {
        name: judgeInfo.name || 'AI Judge',
        email: judgeInfo.email || 'ai@sinarmas.com',
      },
      recaptchaToken
    );

    return {
      success: true,
      submission: {
        id: submission.documentId,
        title: submission.title,
        category: submission.category,
        submitter: submission.full_name,
      },
      judgment: {
        id: submittedJudgment.data.documentId,
        totalScore: judgment.judgmentData.weighted_total_score,
        aspectScores: judgment.judgmentData.aspect_scores,
        summary: judgment.judgmentData.overall_summary,
      },
      rawAIResponse: judgment.aiResponse,
      prompt: judgment.prompt,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      submissionId: submissionDocumentId,
    };
  }
};
/**
 * Create a new VISTA project
 */
export const createVistaProject = async (data: any) => {
  const response = await fetch(`${API_BASE_URL}/api/vista-projects`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${CMS_API_TOKEN}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to create project: ${response.statusText} - ${errorText}`
    );
  }

  return await response.json();
};

/**
 * Update an existing VISTA project
 */
export const updateVistaProject = async (documentId: string, data: any) => {
  const response = await fetch(
    `${API_BASE_URL}/api/vista-projects/${documentId}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${CMS_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update project: ${response.statusText} - ${errorText}`
    );
  }

  return await response.json();
};

/**
 * Create a new VISTA rubric
 */
export const createVistaRubric = async (data: any) => {
  const response = await fetch(`${API_BASE_URL}/api/vista-rubrics`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${CMS_API_TOKEN}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to create rubric: ${response.statusText} - ${errorText}`
    );
  }

  return await response.json();
};

/**
 * Update an existing VISTA rubric
 */
export const updateVistaRubric = async (documentId: string, data: any) => {
  const response = await fetch(
    `${API_BASE_URL}/api/vista-rubrics/${documentId}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${CMS_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update rubric: ${response.statusText} - ${errorText}`
    );
  }

  return await response.json();
};

/**
 * Create a new VISTA aspect
 */
export const createVistaAspect = async (data: any) => {
  const response = await fetch(`${API_BASE_URL}/api/vista-aspects`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${CMS_API_TOKEN}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to create aspect: ${response.statusText} - ${errorText}`
    );
  }

  return await response.json();
};

/**
 * Update an existing VISTA aspect
 */
export const updateVistaAspect = async (documentId: string, data: any) => {
  const response = await fetch(
    `${API_BASE_URL}/api/vista-aspects/${documentId}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${CMS_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update aspect: ${response.statusText} - ${errorText}`
    );
  }

  return await response.json();
};
